"use client";
import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AddBusinessPage from "./AddBusinessPage";
import BusinessDetailPage from "./BusinessDetailPage";
import { ComparePanel } from "./ComparePanel";
import {
  getMarketPlace,
  getMyBusinesses,
} from "../../api/marketPlace/marketPlaceService";
import { IMarketPlace } from "../../api/marketPlace/marketPlaceModel";
import {
  TbPlus,
  TbSearch,
  TbMapPin,
  TbCurrencyDollar,
  TbTrendingUp,
  TbBuildingStore,
  TbUsers,
  TbWorld,
  TbLoader2,
  TbChevronLeft,
  TbChevronRight,
  TbChevronDown,
} from "react-icons/tb";

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmt = (val?: string | null) =>
  val ? `$${Number(val).toLocaleString()}` : "—";

// ── Business Card ──────────────────────────────────────────────────────────────

function BusinessCard({ biz }: { biz: IMarketPlace }) {
  const { t } = useTranslation();
  const hasSocials =
    biz.website_url ||
    biz.instagram_url ||
    biz.facebook_url ||
    biz.linkedin_url ||
    biz.x_url ||
    biz.tiktok_url ||
    biz.youtube_url;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden"
    >
      {/* Cover image */}
      <div className="w-full h-44 shrink-0 bg-gray-100 overflow-hidden">
        {biz.logo_image ? (
          <img
            src={biz.logo_image}
            alt={biz.business_name}
            loading="lazy"
            className="w-full h-full object-cover object-center"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TbBuildingStore className="w-10 h-10 text-gray-300" />
          </div>
        )}
      </div>

      {/* Header strip */}
      <div className="flex items-center gap-3 px-5 pt-4 pb-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {biz.business_name}
          </h3>
          <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-400">
            <TbMapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">
              {[biz.city, biz.country].filter(Boolean).join(", ")}
            </span>
          </div>
        </div>
        <span className="ml-auto shrink-0 text-xs font-medium text-[#020637] bg-[#020637]/5 px-2.5 py-1 rounded-full">
          {biz.year_founded}
        </span>
      </div>

      <div className="min-h-14">
        <p className="px-5 pt-3 pb-1 text-xs text-gray-500 leading-relaxed line-clamp-2 grow">
          {biz.description || t("home.noDescriptionFallback")}
        </p>
      </div>
      {/* Key financials */}
      <div className="grid grid-cols-3 gap-0 px-5 py-4 mt-2">
        <div className="flex flex-col items-center text-center">
          <span className="flex items-center gap-0.5 text-[10px] text-gray-400 mb-0.5">
            <TbCurrencyDollar className="w-3 h-3" />
            {t("home.askingLabel")}
          </span>
          <span className="text-sm font-bold text-gray-900">
            {fmt(biz.asking_price)}
          </span>
        </div>
        <div className="flex flex-col items-center text-center border-x border-gray-100">
          <span className="flex items-center gap-0.5 text-[10px] text-gray-400 mb-0.5">
            <TbTrendingUp className="w-3 h-3" />
            {t("home.revenueMonthlyLabel")}
          </span>
          <span className="text-sm font-semibold text-green-600">
            {fmt(biz.average_monthly_revenue)}
          </span>
        </div>
        <div className="flex flex-col items-center text-center">
          <span className="flex items-center gap-0.5 text-[10px] text-gray-400 mb-0.5">
            <TbUsers className="w-3 h-3" />
            {t("home.clientsLabel")}
          </span>
          <span className="text-sm font-semibold text-gray-700">
            {biz.active_client_count ?? "—"}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 pb-4 flex items-center justify-between">
        <span className="text-[11px] text-gray-400">
          {t("home.netProfitMonthlyLabel")}
          <span className="text-gray-600 font-medium">
            {fmt(biz.average_monthly_net_profit)}
          </span>
        </span>
        {hasSocials && (
          <TbWorld
            className="w-3.5 h-3.5 text-[#020637]"
            title={t("home.hasOnlinePresenceTooltip")}
          />
        )}
      </div>
    </motion.div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────

function EmptyState({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-24 text-center"
    >
      <div className="w-20 h-20 rounded-2xl bg-[#020637]/5 flex items-center justify-center mb-5">
        <TbBuildingStore className="w-10 h-10 text-[#020637]" />
      </div>
      <h2 className="text-lg font-semibold text-gray-800 mb-1">
        {t("home.emptyStateTitle")}
      </h2>
      <p className="text-sm text-gray-400 mb-6 max-w-xs">
        {t("home.emptyStateSubtitle")}
      </p>
      <button
        onClick={onAdd}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors"
      >
        <TbPlus className="w-4 h-4" />
        {t("home.listBusinessButton")}
      </button>
    </motion.div>
  );
}

const rowsOptions = [
  { label: "5 per page", value: 5 },
  { label: "9 per page", value: 9 },
  { label: "15 per page", value: 15 },
  { label: "20 per page", value: 20 },
];

export default function Marketplace() {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);
  const [selectedBiz, setSelectedBiz] = useState<IMarketPlace | null>(null);
  const [businesses, setBusinesses] = useState<IMarketPlace[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isFiltered, setIsFiltered] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [totalCount, setTotalCount] = useState(0);
  const [showRowsDropdown, setShowRowsDropdown] = useState(false);
  // Controls the expandable "Why Halper?" comparison panel in the header.
  // Opens automatically on page load (auto-closed after 5s by the effect below).
  const [showCompareModal, setShowCompareModal] = useState(true);

  useEffect(() => {
    const handleOpen = () => {
      setShowAdd(false);
      setSelectedBiz(null);
      setShowCompareModal(true);
    };
    window.addEventListener("openMarketplaceCompareModal", handleOpen);
    return () => {
      window.removeEventListener("openMarketplaceCompareModal", handleOpen);
    };
  }, []);

  // Auto-close the comparison panel 5s after the page opens.
  useEffect(() => {
    const timer = setTimeout(() => setShowCompareModal(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const fetchBusinesses = async (pageNum = 1) => {
    try {
      setLoading(true);
      const res = await getMarketPlace(pageNum, rowsPerPage);
      const data = Array.isArray(res.data.results) ? res.data.results : [];
      setBusinesses(data);
      setTotalCount(res.data.pagination?.total_items || 0);
      setPage(pageNum);
    } catch {
      setBusinesses([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBusinesses = async (pageNum = 1) => {
    try {
      setLoading(true);
      const res = await getMyBusinesses(pageNum, rowsPerPage);
      const data = Array.isArray(res.data.results) ? res.data.results : [];
      setBusinesses(data);
      setTotalCount(res.data.pagination?.total_items || 0);
      setPage(pageNum);
    } catch {
      setBusinesses([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setShowRowsDropdown(false);
    setPage(1);
    if (isFiltered) {
      fetchMyBusinesses(1);
    } else {
      fetchBusinesses(1);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);
  let filtered = businesses.filter((b) =>
    [b.business_name, b.city, b.country, b.description]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  const onMyBusinessClick = () => {
    setIsFiltered((prev) => !prev);
    if (!isFiltered) {
      fetchMyBusinesses(1);
    } else {
      fetchBusinesses(1);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (isFiltered) {
      fetchMyBusinesses(newPage);
    } else {
      fetchBusinesses(newPage);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {showAdd ? (
        <AddBusinessPage
          key="add"
          onBack={() => {
            setShowAdd(false);
            fetchBusinesses(1);
          }}
        />
      ) : selectedBiz ? (
        <BusinessDetailPage
          key={`detail-${selectedBiz.id}`}
          biz={selectedBiz}
          onBack={() => {
            fetchBusinesses(page);
            setSelectedBiz(null);
          }}
          onUpdated={(updated) => {
            setSelectedBiz(updated);
            setBusinesses((prev) =>
              prev.map((b) => (b.id === updated.id ? updated : b)),
            );
          }}
          onDeleted={(id) => {
            setBusinesses((prev) => prev.filter((b) => b.id !== id));
            setSelectedBiz(null);
          }}
        />
      ) : (
        <motion.div
          key="listing"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="min-h-screen bg-gradient-to-br from-white to-gray-50 flex flex-col"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-100 px-4 sm:px-6 py-3 sm:py-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t("home.marketplaceTitle")}</h1>
                <p className="text-xs text-gray-400 mt-0.5">
                  {loading
                    ? t("home.loadingListingsStatus")
                    : t("home.businessesCountLabel", { count: totalCount, page, totalPages: totalPages || 1 })}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setShowCompareModal((v) => !v)}
                  aria-expanded={showCompareModal}
                  className="group flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl border border-gray-200 bg-white text-xs sm:text-sm font-medium text-gray-700 hover:border-[#020637] hover:text-[#020637] transition-colors shrink-0"
                >
                  {t("home.compareButton")}
                  <TbChevronDown
                    className={`w-4 h-4 shrink-0 transition-transform duration-300 ${
                      showCompareModal ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div className="flex gap-2 w-full sm:contents">
                  <button
                    onClick={onMyBusinessClick}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl bg-gray-900 text-white text-xs sm:text-sm font-medium hover:bg-gray-700 transition-colors shrink-0"
                  >
                    {isFiltered ? t("home.showAllButton") : t("home.myBusinessButton")}
                  </button>
                  <button
                    onClick={() => setShowAdd(true)}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl bg-gray-900 text-white text-xs sm:text-sm font-medium hover:bg-gray-700 transition-colors shrink-0"
                  >
                    <TbPlus className="w-4 h-4 shrink-0" />
                    {t("home.addBusinessButton")}
                  </button>
                </div>
              </div>
              </div>

              <AnimatePresence initial={false}>
                {showCompareModal && (
                  <motion.div
                    key="compare-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <ComparePanel />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 flex-grow flex flex-col gap-6">
            {/* Search */}
            <div className="relative">
              <TbSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t("home.searchPlaceholder")}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/15 transition-all"
              />
            </div>

            {/* Grid */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <TbLoader2 className="w-8 h-8 text-[#020637] animate-spin mb-3" />
                <p className="text-sm text-gray-600 font-medium">
                  {t("home.loadingBusinessesStatus")}
                </p>
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState onAdd={() => setShowAdd(true)} />
            ) : (
              <div className="flex-grow flex flex-col justify-between gap-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map((biz, i) => (
                    <div
                      key={biz.id ?? i}
                      onClick={() => setSelectedBiz(biz)}
                      className="cursor-pointer"
                    >
                      <BusinessCard biz={biz} />
                    </div>
                  ))}
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-center gap-2 mt-auto pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <TbChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }).map(
                      (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                              page === pageNum
                                ? "bg-[#020637] text-white"
                                : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      },
                    )}
                  </div>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <TbChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

